import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, BadgeCheck, Phone, ShieldCheck, Sparkles } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { HERO_SLIDES } from "@/data/heroSlides";

const ease = [0.22, 1, 0.36, 1];
const pillDark =
  "inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-3 py-1 text-xs font-semibold text-white ring-1 ring-white/20";

const Hero = () => {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (reduce || HERO_SLIDES.length <= 1) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(id);
  }, [reduce]);

  return (
    <section className="relative min-h-[88vh] flex items-center overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 -z-10 bg-slate-900">
        <AnimatePresence>
          <motion.img
            key={index}
            src={HERO_SLIDES[index]}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease }}
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/55 to-slate-900/35" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/70 to-transparent" />
      </div>

      <div className="container-x w-full py-20 sm:py-28">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease }}
          className="max-w-2xl"
        >
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className={pillDark}><Sparkles className="h-3.5 w-3.5 text-emerald-300" />{t("hero.badge")}</span>
            <span className={pillDark}><ShieldCheck className="h-3.5 w-3.5 text-emerald-300" />{t("hero.badge_rc_pro")}</span>
            <span className={pillDark}><BadgeCheck className="h-3.5 w-3.5 text-emerald-300" />{t("hero.badge_cesu")}</span>
          </div>

          <h1 className="font-heading font-extrabold tracking-tighter text-3xl sm:text-4xl md:text-5xl lg:text-[4.25rem] text-white leading-[1.04]">
            {t("hero.title")}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl">
            {t("hero.subtitle")}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/devis" className="btn-primary group" data-testid="hero-quote-cta">
              {t("common.cta_quote")}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <a
              href="tel:+33649502801"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 backdrop-blur px-5 py-3 text-sm font-semibold text-white ring-1 ring-white/30 hover:bg-white/20 transition"
              data-testid="hero-phone-cta"
            >
              <Phone className="h-4 w-4" /> 06 49 50 28 01
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3">
            {[
              { v: "4,9/5", l: t("hero.stat_rating") },
              { v: "250+",  l: t("hero.stat_interventions") },
              { v: "24/7",  l: t("hero.stat_availability") },
            ].map(({ v, l }) => (
              <div key={l}>
                <div className="font-heading font-extrabold text-2xl text-emerald-300 leading-none">{v}</div>
                <div className="text-xs text-slate-300 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {HERO_SLIDES.map((_, i) => (
          <button
            type="button"
            key={i}
            onClick={() => setIndex(i)}
            aria-label={`Image ${i + 1}`}
            className={`h-2 rounded-full transition-all ${i === index ? "w-6 bg-white" : "w-2 bg-white/50 hover:bg-white/80"}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;
